<!-- eslint-disable vue/no-v-html -->
<template>
  <b-modal
    id="modal-authorization"
    :title="getActionName"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    hide-footer
    centered
    size="sm"
    @shown="onShown"
    @hidden="onHideActions"
  >
    <FormulateForm
      ref="formDiscount"
      name="formDiscount"
      @submit="onConfirm"
    >
      <b-container>
        <b-row
          v-if="errorDescription"
          class="pb-1"
        >
          <b-alert
            show
            variant="dark"
            style="padding: 15px"
            v-html="errorDescription"
          />
        </b-row>

        <b-row align-h="center">
          <b-col
            md="9"
            class="d-flex flex-column"
          >
            <FormulateInput
              id="modalUserInput"
              v-model="form.user"
              name="user"
              class="w-100 required"
              :type="isPdvAtm ? 'osk-text' : 'text'"
              :label="$t('Usuário')"
              validation="required"
            />

            <FormulateInput
              v-model="form.password"
              name="password"
              class="w-100 required"
              :type="isPdvAtm ? 'osk-text-password' : 'password'"
              :label="$t('Senha')"
              validation="required"
            />
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col class="d-flex justify-content-end">
            <e-button
              class="mr-1"
              :text="$t('Cancelar')"
              :text-shortcuts="isPdvAtm ? [] : ['ESC']"
              variant="danger"
              @click="onCancel"
            />
            <e-button
              variant="primary"
              type="submit"
              :busy="busy"
              :text="$t('Confirmar')"
            />
          </b-col>
        </b-row>
        <label
          v-show="capsLockState"
          class="text-danger"
        >A tecla CapsLock está ligada!</label>
      </b-container>
    </FormulateForm>
  </b-modal>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import { getUserData, hasPermissions } from '@/auth/utils'
import { BModal, BContainer, BRow, BCol, BAlert } from 'bootstrap-vue'
import EButton from '@/views/components/EButton.vue'
import { formulateHelper, keyboardCode } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import { getApiErrorDataMessage } from '@/utils/error-utils'
import { Shortcuts } from 'shortcuts'

export default {
  components: { BModal, BContainer, BRow, BCol, BAlert, EButton },

  mixins: [formulateHelper, keyboardCode],

  data() {
    return {
      busy: false,
      isSync: false,
      authorizationPromise: null,
      modalResolve: null,
      modalReject: null,
      localStoreId: null,
      actionName: '',
      delegatePermission: '',
      form: {
        user: '',
        password: '',
      },
      errorDescription: '',
      actionCallback: null,
      message: '',
      shortcuts: new Shortcuts(),
    }
  },

  computed: {
    ...mapState('app/keyboard', ['capsLockState']),
    ...mapGetters('pages/pdv/payBoxConfiguration', ['currentPayboxConfiguration', 'isPdvAtm']),
    getActionName() {
      return this.$t('Autorizar Ação: ') + this.$t(this.actionName)
    },
  },

  methods: {
    ...mapActions('common/permissions', ['getPermissionMessage']),
    async setMessagePermission(action) {
      this.message = ''
      if (action.doNotShowProfilesWithPermission) return
      this.message = await this.getPermissionMessage(action.permission)
    },
    async onConfirm() {
      try {
        this.busy = true
        const userData = getUserData()
        const storeId = this.localStoreId || this.currentPayboxConfiguration?.store?.id

        const { data: delegateUserData } = await useJwt.delegatePermission({
          userName: this.form.user,
          password: this.form.password,
          delegateUserName: userData.userName,
          delegatePermission: this.delegatePermission,
          storeId,
        })
        if (this.actionCallback) await this.actionCallback(delegateUserData)
        const resolve = this.modalResolve
        this.modalResolve = null
        this.modalReject = null
        if (resolve) resolve(delegateUserData)
        this.$bvModal.hide('modal-authorization')
      } catch (error) {
        this.showError({
          message: error.response
            ? this.$t(error.response.data)
            : this.$t('Usuário não autorizado para esta ação'),
        })
      } finally {
        this.busy = false
      }
    },

    resetForm() {
      this.form = {
        user: '',
        password: '',
      }
      this.errorDescription = null
      this.localStoreId = null
      this.actionCallback = null
    },

    onCancel() {
      this.onHideModal()
      if (this.isSync) {
        this.showError({ message: 'Autorização cancelada' })
      }
    },

    onHideModal() {
      if (this.modalReject) this.modalReject({ message: 'Autorização cancelado' })
      this.isSync = false
      this.shortcuts.reset()
      this.$bvModal.hide('modal-authorization')
    },

    onHideActions() {},

    async auth(action, storeId) {
      if (hasPermissions(action.permission)) return undefined
      if (this.authorizationPromise) return this.authorizationPromise

      this.addShortcuts()

      const authorizationPromise = (async () => {
        await this.setMessagePermission(action)
        return new Promise((resolve, reject) => {
          this.actionName = action.name
          this.delegatePermission = action.permission
          this.resetForm()
          this.errorDescription = this.message
          if (storeId) this.localStoreId = storeId
          this.modalReject = reject
          this.modalResolve = resolve
          this.$bvModal.show('modal-authorization')
        })
      })()

      this.authorizationPromise = authorizationPromise
      authorizationPromise.then(
        () => {
          this.authorizationPromise = null
        },
        () => {
          this.authorizationPromise = null
        }
      )
      return authorizationPromise
    },

    /**
     * @param {*} options.confirmCallback (delegatePermission) => {}
     * @param {Boolean} options.withHasPermission
     * @param {*} options.errorData error data get from getApiErrorData method
     */
    authSync(
      action,
      storeId,
      options = { confirmCallback: null, withHasPermission: true, errorData: null }
    ) {
      this.addShortcuts()

      const { confirmCallback, withHasPermission, errorData } = options

      if (withHasPermission && hasPermissions(action.permission)) {
        return
      }
      this.isSync = true
      this.resetForm()
      this.actionName = action.name
      this.delegatePermission = action.permission
      if (storeId) this.localStoreId = storeId
      if (confirmCallback) this.actionCallback = confirmCallback
      if (errorData) this.errorDescription = getApiErrorDataMessage(errorData)

      this.$bvModal.show('modal-authorization')
    },

    onShown() {
      this.focusInput('#modalUserInput')
    },

    addShortcuts() {
      const { esc } = this.keyCode

      this.shortcuts.add([
        {
          shortcut: esc,
          handler: () => {
            this.onHideModal()
            return true
          },
        },
      ])
    },
  },
}
</script>

<style lang="scss" scoped></style>
